/**
 * inspectionKey.ts
 * Identifiant déterministe pour lier photos, brouillons et données EDL à un contrat.
 *
 * Format : "dep-{numeroContrat}" | "retour-{numeroContrat}"
 *
 * Règle : le numéro de contrat (numeroDocument) est TOUJOURS la source de vérité.
 * Aucun ID interne MRC ne doit être utilisé comme clé de stockage.
 *
 * @version 1.0.0
 */

export type InspectionType = 'depart' | 'retour'

export interface ContractLike {
  numeroDocument?: string | null
  id?: string | number
  rawId?: string | number
}

/**
 * Construit l'identifiant unique d'une inspection.
 *
 * @param type     'depart' | 'retour'
 * @param contract Objet contrat contenant au minimum `numeroDocument`
 * @returns        ex: "dep-CD25-04567" ou "retour-CD25-04567"
 * @throws         Si `numeroDocument` est absent
 */
export function makeInspectionKey(
  type: InspectionType,
  contract: ContractLike
): string {
  const prefix = type === 'depart' ? 'dep' : 'retour'
  let numero = contract?.numeroDocument

  if (!numero || typeof numero !== 'string' || numero.trim() === '') {
    // Fallback to contract.id for preparation states and old contracts
    numero = String(contract?.rawId ?? contract?.id)
    if (!numero || numero === 'undefined' || numero === 'null') {
      throw new Error(
        `[makeInspectionKey] numeroDocument et id manquants — impossible de créer une clé fiable. ` +
        `contract.id=${contract?.id}, type=${type}`
      )
    }
  }

  return `${prefix}-${numero.trim()}`
}

/**
 * Version safe qui retourne null au lieu de throw.
 * Utilisée dans les guards et conditions.
 */
export function tryMakeInspectionKey(
  type: InspectionType,
  contract: ContractLike
): string | null {
  try {
    return makeInspectionKey(type, contract)
  } catch {
    return null
  }
}

/**
 * Extrait le type et le numéro de contrat depuis une clé d'inspection.
 *
 * @param key  ex: "dep-CD25-04567" ou "retour-CD25-04567"
 * @returns    { type, numero } ou null si format invalide
 */
export function parseInspectionKey(
  key: string
): { type: InspectionType; numero: string } | null {
  if (!key || typeof key !== 'string') return null
  const match = key.match(/^(dep|retour)-(.+)$/)
  if (!match) return null
  return {
    type: match[1] === 'dep' ? 'depart' : 'retour',
    numero: match[2],
  }
}

/**
 * Vérifie si une chaîne est au format inspectionKey valide.
 */
export function isInspectionKey(value: unknown): value is string {
  if (typeof value !== 'string') return false
  return /^(dep|retour)-.+$/.test(value)
}

/**
 * Helper legacy : extrait le contractId le plus fiable depuis un objet contrat.
 * Utilisé UNIQUEMENT pour les appels API MRC qui nécessitent l'ID numérique.
 * NE PAS utiliser pour le stockage interne.
 */
export function getMrcContractId(contract: ContractLike): string {
  const id = contract?.rawId ?? contract?.id
  if (!id) throw new Error('[getMrcContractId] Aucun ID contrat disponible')
  return String(id)
}
